import {
  Stack, TextInput, NumberInput, Select, MultiSelect,
  Textarea, Switch,
} from '@mantine/core'

export interface CustomField {
  name: string
  label: string
  type: 'text' | 'number' | 'select' | 'multiselect' | 'textarea' | 'date' | 'boolean'
  required?: boolean
  options?: string[]
  placeholder?: string
  help?: string
  pattern?: string
  required_when?: { field: string; equals: any }
  visible_when?: { field: string; equals: any }
}

interface Props {
  schema: CustomField[]
  values: Record<string, any>
  onChange: (values: Record<string, any>) => void
  disabled?: boolean
}

const coerce = (v: any) => (v === 'true' ? true : v === 'false' ? false : v)

export function CustomFieldsForm({ schema, values, onChange, disabled }: Props) {
  const handleChange = (name: string, value: any) => {
    onChange({ ...values, [name]: value })
  }

  const condMet = (c?: { field: string; equals: any }) =>
    !c || coerce(values[c.field]) === coerce(c.equals)

  if (!schema || schema.length === 0) return null

  return (
    <Stack gap="sm">
      {schema.map((field) => {
        const key = field.name
        if (!condMet(field.visible_when)) return null
        const required = field.required || (!!field.required_when && condMet(field.required_when))
        switch (field.type) {
          case 'text': {
            const val = values[key] ?? ''
            const bad = field.pattern && val && !new RegExp(field.pattern).test(val)
            return (
              <TextInput
                key={key}
                label={field.label}
                description={field.help}
                placeholder={field.placeholder}
                required={required}
                disabled={disabled}
                value={val}
                error={bad ? 'Formato inválido' : undefined}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            )
          }
          case 'number':
            return (
              <NumberInput
                key={key}
                label={field.label}
                description={field.help}
                placeholder={field.placeholder}
                required={required}
                disabled={disabled}
                value={values[key] ?? ''}
                onChange={(val) => handleChange(key, val)}
              />
            )
          case 'select':
            return (
              <Select
                key={key}
                label={field.label}
                description={field.help}
                placeholder={field.placeholder}
                required={required}
                disabled={disabled}
                data={field.options ?? []}
                value={values[key] ?? null}
                onChange={(val) => handleChange(key, val)}
                clearable
              />
            )
          case 'multiselect':
            return (
              <MultiSelect
                key={key}
                label={field.label}
                description={field.help}
                placeholder={field.placeholder}
                required={required}
                disabled={disabled}
                data={field.options ?? []}
                value={values[key] ?? []}
                onChange={(val) => handleChange(key, val)}
                clearable
              />
            )
          case 'textarea':
            return (
              <Textarea
                key={key}
                label={field.label}
                description={field.help}
                placeholder={field.placeholder}
                required={required}
                disabled={disabled}
                rows={3}
                value={values[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            )
          case 'date':
            return (
              <TextInput
                key={key}
                type="date"
                label={field.label}
                description={field.help}
                placeholder={field.placeholder}
                required={required}
                disabled={disabled}
                value={values[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            )
          case 'boolean':
            return (
              <Switch
                key={key}
                label={field.label}
                description={field.help}
                required={required}
                disabled={disabled}
                checked={coerce(values[key]) === true}
                onChange={(e) => handleChange(key, e.currentTarget.checked)}
              />
            )
          default:
            return null
        }
      })}
    </Stack>
  )
}
